import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { wrapRequest } from 'helpers/requests';

import { media } from 'context';
import { Layout, Link, RequestState, Text } from 'ds';
import { submitFeedback } from 'ds/FeedbackRequests/requests';
import { PANTRY_ORDERS_PATH } from 'routes';
import { actions as dashboardActions } from 'store/Dashboard';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { selectLoggedInUserId } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import ConsumablesOrderProductPreview from 'ux/Tenant/PantryRestocking/ConsumablesOrderProductPreview';

import FeedbackSubwidget from './FeedbackSubwidget';
import OrderProgressBar from './OrderProgressBar';
import { getDashboardWidgetConfigs } from './requests';
import { ConsumablesOrderWidgetData, WidgetVariant } from './types';
import { formatDeliveryDate, getConsumablesOrderProgressPercent, getRelativeDateWord } from './utils';

interface Props {
  data: ConsumablesOrderWidgetData;
  variant: WidgetVariant;
}

let animateProgressBar = true;

const ConsumablesOrderWidget: React.FC<Props> = ({
  data: { id, delivery_date, items: itemsRaw, status: statusRaw, task_id, feedback },
  variant = 'full'
}) => {
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  const status = statusRaw ?? 'open';
  const { isMobile } = useContext(media);
  const userId = useSelector(selectLoggedInUserId);
  const tenancyId = useSelector(selectTenancyId);
  const progressPercent = getConsumablesOrderProgressPercent(status);
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const deliveryTextByStatus = {
    open: 'Order open',
    locked: 'Order processing',
    delivered: formatDeliveryDate({ date: delivery_date, format: { month: 'short', day: 'numeric' }, status }),
    canceled: 'Canceled'
  } as const;
  const deliveryText = deliveryTextByStatus[status as keyof typeof deliveryTextByStatus] ?? 'Order open';
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const dispatch = useAppDispatch();

  // Transform dashboard widget items to match ConsumablesOrderProductPreview format
  const transformedItems = items.map(item => ({
    consumables_product: {
      image_key: item.image?.key
    }
  })) as any[];
  useEffect(() => {
    setTimeout(() => {
      animateProgressBar = false;
    }, 1000);
  }, []);
  const handleFeedbackSubmit = async (liked: boolean) => {
    if (!task_id || !tenancyId || !widgetConfigs) return;

    setRequestState('in_progress');
    await wrapRequest(
      submitFeedback({
        userId,
        feedback: { liked, task_id }
      }),
      { successMessage: 'Feedback submitted.' }
    );

    const updatedWidgetConfigs = widgetConfigs.map(row =>
      row.map(config => {
        if (config.type === 'consumables_order' && config.data?.id === id) {
          return {
            ...config,
            data: {
              ...config.data,
              feedback: { liked }
            }
          };
        }
        return config;
      })
    );

    dispatch(dashboardActions.setWidgetConfigs(updatedWidgetConfigs));
    setTimeout(async () => {
      const widgetConfigs = await getDashboardWidgetConfigs({ tenancyId });
      dispatch(dashboardActions.setWidgetConfigs(widgetConfigs));
    }, 1000);
  };

  if (variant === 'half') {
    return (
      <Link href={`${PANTRY_ORDERS_PATH}/${id}`} color="black" fullWidth fullHeight>
        <Layout borderRadius={16} color="white" padding={12} align="center" gap={16} height="100%">
          <Layout width={56} height={56} padding={4} color="white" borderRadius={8} align="center" justify="center">
            <ConsumablesOrderProductPreview consumablesOrderItems={transformedItems} size="small" />
          </Layout>
          <Layout direction="column" gap={4} flexGrow={1}>
            <Text size="body-xs">{isMobile ? 'Restocking' : 'Restocking order'}</Text>
            {!isMobile && <OrderProgressBar progressPercent={progressPercent} animate={animateProgressBar} />}
            {items.length === 0 ? (
              <Text size="body-xs" color="blue-500">
                Add items
              </Text>
            ) : (
              <Text size="body-xs" color="gray-400">
                {deliveryText}
              </Text>
            )}
          </Layout>
        </Layout>
      </Link>
    );
  }

  return (
    <Link href={`${PANTRY_ORDERS_PATH}/${id}`} color="black" search={`?id=${id}`} fullWidth fullHeight>
      <Layout borderRadius={16} direction="column" color="white" padding={12} height="100%" gap={10}>
        <Layout borderRadius={8} color="white" padding={12} height={84} align="center" justify="center">
          <ConsumablesOrderProductPreview
            consumablesOrderItems={transformedItems}
            size="default"
            useHorizontalMobileLayout={true}
            maxVisibleItems={2}
          />
        </Layout>
        {status === 'delivered' && task_id ? (
          <Layout flexGrow={1}>
            <FeedbackSubwidget
              disabled={requestState === 'in_progress'}
              text={`${items.length} items restocked ${getRelativeDateWord(delivery_date)}`}
              handleFeedback={handleFeedbackSubmit}
              liked={feedback?.liked}
              includeModal={false}
            />
          </Layout>
        ) : status === 'canceled' ? (
          <Layout direction="column" gap={4} flexGrow={1}>
            <Text size="body-xs">Restocking order</Text>
            <Text size="body-xs" color="gray-400">
              {deliveryText}
            </Text>
          </Layout>
        ) : (
          <Layout direction="column" gap={4} flexGrow={1}>
            <Text size="body-xs">Restocking order</Text>
            <OrderProgressBar progressPercent={progressPercent} animate={animateProgressBar} />
            {items.length === 0 ? (
              <Text size="body-xs" color="blue-500">
                Add items
              </Text>
            ) : (
              <Text size="body-xs" color="gray-400">
                {deliveryText}
              </Text>
            )}
          </Layout>
        )}
      </Layout>
    </Link>
  );
};

export default ConsumablesOrderWidget;
